import { useEffect, useState } from "react";
import useUpdateRouteQuery from "../../../hooks/useUpdateRouteQuery";
import DisplayComponent from "./pagination/DisplayComponent";
import PageNumberButton from "./pagination/PageNumberButton";
import PaginationControls from "./pagination/PaginationControls";
import PaginationPrevNextControls from "./pagination/PaginationPrevNextControlsProps ";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import RowsPerPageSelector from "./pagination/RowsPerPageSelector";

export default function Pagination({
  totalRows,
  currentPage,
  rowsPerPage,
  currentPath,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageDisabled,
}) {
  const updateRouteQuery = useUpdateRouteQuery();
  const totalPages = Math.ceil(totalRows / rowsPerPage);
  const pageNumbers = Array.from(Array(totalPages).keys(), (x) => x + 1);
  const [manualPageInput, setManualPageInput] = useState(
    currentPage.toString()
  );

  useEffect(() => {
    setManualPageInput(currentPage.toString());
  }, [currentPage]);

  let startIndex, endIndex;
  if (totalPages <= 5) {
    startIndex = 0;
    endIndex = totalPages - 1;
  } else if (currentPage <= 3) {
    startIndex = 0;
    endIndex = 4;
  } else if (currentPage >= totalPages - 2) {
    startIndex = totalPages - 5;
    endIndex = totalPages - 1;
  } else {
    startIndex = currentPage - 3;
    endIndex = currentPage + 1;
  }

  const displayedPages = pageNumbers.slice(startIndex, endIndex + 1);

  // handling pagination
  const isPrevDisabled = () => currentPage <= 1;
  const isNextDisabled = () => currentPage >= totalPages;

  const handlePrevNextPaginate = (direction) => {
    if (direction === -1 && isPrevDisabled()) {
      return;
    }
    if (direction === 1 && isNextDisabled()) {
      return;
    }
    updateRouteQuery({
      query: { page: currentPage + direction },
      pathName: currentPath,
    });
    onPageChange(currentPage + direction);
  };

  const handleNumberPaginate = (clickedPage) => {
    updateRouteQuery({
      query: { page: clickedPage },
      pathName: currentPath,
    });
    onPageChange(clickedPage);
  };

  const handleManualInputSubmit = () => {
    const inputPage = parseInt(manualPageInput, 10);
    if (!isNaN(inputPage) && inputPage >= 1 && inputPage <= totalPages) {
      handleNumberPaginate(inputPage);
      setManualPageInput(inputPage.toString());
    }
  };

  const from = totalRows ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const to =
    (currentPage - 1) * rowsPerPage + rowsPerPage < totalRows
      ? (currentPage - 1) * rowsPerPage + rowsPerPage
      : totalRows;

  return (
    <div
      className={`flex items-center justify-between p-3 pb-1 bg-white sm:px-2 text-sm font-medium text-black mt-2`}
    >
      <PaginationControls
        onClick={() => handlePrevNextPaginate(-1)}
        direction={-1}
        disabled={isPrevDisabled()}
      />
      <div className="flex justify-between flex-1 sm:hidden">
        <PaginationPrevNextControls
          onClick={handlePrevNextPaginate}
          direction={-1}
          disabled={isPrevDisabled()}
          currentPage={currentPage}
          totalPages={totalPages}
        />
        <PaginationPrevNextControls
          onClick={handlePrevNextPaginate}
          direction={1}
          disabled={isNextDisabled()}
          currentPage={currentPage}
          totalPages={totalPages}
        />
      </div>
      <div className="flex flex-col items-center gap-2 hidden sm:flex">
        <nav
          className="relative z-0 flex items-center gap-3 -space-x-px rounded-[4px] h-[30px] hidden sm:flex"
          aria-label="Pagination"
        >
          {totalPages > 5 && currentPage > 3 && (
            <>
              <PageNumberButton
                pageNumber={1}
                currentPage={currentPage}
                onClick={handleNumberPaginate}
              />
              {currentPage > 4 && (
                <span
                  className={`relative inline-flex items-center p-1 min-w-[30px] h-[30px] justify-center -ml-px cursor-default text-primary`}
                >
                  ...
                </span>
              )}
            </>
          )}
          {displayedPages.map((page) => (
            <PageNumberButton
              key={page}
              pageNumber={page}
              currentPage={currentPage}
              onClick={handleNumberPaginate}
            />
          ))}
          {totalPages > 5 && currentPage < totalPages - 2 && (
            <>
              {currentPage < totalPages - 3 && (
                <span
                  className={`relative inline-flex items-center p-1 min-w-[30px] h-[30px] justify-center -mr-px  cursor-default `}
                >
                  ...
                </span>
              )}
              <PageNumberButton
                pageNumber={totalPages}
                currentPage={currentPage}
                onClick={handleNumberPaginate}
              />
            </>
          )}
          {totalPages > 5 && currentPage < totalPages - 2 && (
            <div className="flex items-center !ml-4">
              <label htmlFor="manualPageInput" className={`mr-2`}>
                Go to
              </label>
              <input
                id="manualPageInput"
                type="number"
                value={manualPageInput}
                onChange={(e) => setManualPageInput(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" && handleManualInputSubmit()
                }
                className="w-[60px] p-1 border border-gray-300 rounded-[4px]"
                max={totalPages}
                min={1}
                // placeholder={`${currentPage}/${totalPages}`}
              />
              <button
                onClick={handleManualInputSubmit}
                className="border-2 rounded-[4px] relative ml-2 items-center p-3.5 min-w-[30px] h-[30px] justify-center text-sm font-medium gap-2 hidden sm:flex bg-white text-black hover:bg-gray-100 focus:outline-none focus:ring-2 focus:text-primary hover:text-primary"
              >
                <FontAwesomeIcon
                  icon={faArrowRight}
                  className="w-3 h-3"
                  aria-hidden="true"
                />
              </button>
            </div>
          )}
        </nav>
        <div className="flex flex-col gap-4 sm:flex-row sm:gap-8 items-center">
          <DisplayComponent from={from} to={to} totalRows={totalRows} />
          <div className="flex items-center gap-4">
            {onRowsPerPageChange && (
              <RowsPerPageSelector
                totalRows={totalRows}
                rowsPerPage={rowsPerPage}
                currentPath={currentPath}
                onRowsPerPageChange={onRowsPerPageChange}
                disabled={rowsPerPageDisabled}
              />
            )}
          </div>
        </div>
      </div>

      <PaginationControls
        onClick={() => handlePrevNextPaginate(1)}
        direction={1}
        disabled={isNextDisabled()}
      />
    </div>
  );
}
